/** @file Mix preview controls: the seconds shown, start and stop, and saving the pair's default. */
import type { SkeletonData } from '@pose2d/core/types';
import type { CommandCall, Doc } from '@pose2d/format/types';
import { mixPreview } from '../../playback/mixPreview';
import { mixPreviewStore, type MixPreviewState } from '../../playback/mixPreviewStore';
import { playhead } from '../../playback/playhead';
import { animationCalls } from './animationCalls';

/** The picked pair and typed seconds (what the seconds field depends on). */
type MixPick = Pick<MixPreviewState, 'from' | 'to' | 'seconds'>;

/** Names of the picked pair, or null until both are picked and exist. */
function pairOf(doc: Doc, s: MixPick): [string, string] | null {
  const from = s.from === null ? undefined : doc.animations[s.from]?.name;
  const to = s.to === null ? undefined : doc.animations[s.to]?.name;
  return from === undefined || to === undefined ? null : [from, to];
}

/** Store-backed actions of the MixPreview section. */
export const mixPreviewActions = {
  /** Typed seconds, else core's default for the pair, else 0. */
  seconds(doc: Doc, data: SkeletonData | null, s: MixPick): number {
    const pair = pairOf(doc, s);
    if (s.seconds !== null || pair === null || data === null) return s.seconds ?? 0;
    return data.animationIndex.has(pair[0]) && data.animationIndex.has(pair[1]) ? mixPreview.defaultSeconds(data, pair[0], pair[1]) : 0;
  },
  /** Pauses the playhead and starts a run (nothing when the pair is incomplete or not compiled yet). */
  start(doc: Doc, data: SkeletonData | null): void {
    const s = mixPreviewStore.getState();
    const pair = pairOf(doc, s);
    const run = pair === null || data === null ? null : mixPreview.start(data, pair[0], pair[1], s.seconds);
    if (run === null) return;
    playhead.pause();
    mixPreviewStore.setState({ run });
  },
  /** Ends the run; the viewport shows the playhead again. */
  stop(): void {
    mixPreviewStore.setState({ run: null });
  },
  /** animation.setMixDefault with the seconds shown (empty when the pair is incomplete). */
  saveCalls(doc: Doc, data: SkeletonData | null): CommandCall[] {
    const s = mixPreviewStore.getState();
    const pair = pairOf(doc, s);
    return pair === null ? [] : [animationCalls.saveMix(pair[0], pair[1], mixPreviewActions.seconds(doc, data, s))];
  },
};
